// 使用原生 fetch API，更簡單可靠
// 動態獲取 API URL
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  // When accessed via reverse proxy (no port or standard 80/443), use same origin
  if (!port || port === '80' || port === '443') {
    return `${window.location.protocol}//${hostname}/api/v1`;
  }
  // Local dev: port 3000 → backend on 8000
  if (port === '3000') {
    return `http://${hostname}:8000/api/v1`;
  }
  return `http://${hostname}:${port}/api/v1`;
};

const API_BASE_URL = getApiBaseUrl();
console.log('🔗 API Base URL:', API_BASE_URL);

// 通用請求函數
async function request(url, options = {}) {
  const fullUrl = `${API_BASE_URL}${url}`;
  console.log(`📤 ${options.method || 'GET'} 請求:`, fullUrl);

  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('spo_auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Support custom timeout (default: no timeout)
    const { timeout, ...fetchOptions } = options;
    let signal = fetchOptions.signal;
    let timeoutId;
    if (timeout && !signal) {
      const controller = new AbortController();
      signal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      signal,
      headers: {
        ...headers,
        ...fetchOptions.headers,
      },
    });
    if (timeoutId) clearTimeout(timeoutId);

    console.log(`📥 回應狀態:`, response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => {
        // Some proxies return an HTML error page (502/504) instead of JSON
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          return { detail: `伺服器回應錯誤 (HTTP ${response.status})，請稍後再試或聯繫管理員` };
        }
        return { detail: response.statusText };
      });
      if (response.status === 402 && (error?.detail?.error === "subscription_required" || error?.error === "subscription_required")) {
        window.dispatchEvent(new CustomEvent("spo:subscription_required", { detail: error }));
      }
      console.error('❌ API 錯誤:', error);
      throw new Error((typeof error.detail === 'string' ? error.detail : error.detail?.message || JSON.stringify(error)) || `HTTP ${response.status}`);
    }

    // Success path — but some misconfigured proxies return the SPA index.html with HTTP 200.
    // Detect that and fail with a clear message instead of a cryptic JSON parse error.
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      console.error('❌ 伺服器回傳 HTML 而非 JSON（可能連到前端而非後端 API）:', fullUrl);
      throw new Error(`伺服器回應格式異常：請求 ${url} 未連到後端 API，請確認網域已正確配置`);
    }

    const data = await response.json().catch(() => {
      console.error('❌ 非 JSON 回應，URL:', fullUrl, 'status:', response.status, 'content-type:', contentType);
      throw new Error(`伺服器回應格式異常 (HTTP ${response.status})，請稍後再試或聯繫管理員`);
    });
    console.log('✅ 回應成功:', data);
    return { data };
  } catch (error) {
    console.error('❌ 請求失敗:', error);
    throw error;
  }
}

// Products API
export const productsAPI = {
  getAll: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/products/products${queryString ? `?${queryString}` : ''}`);
  },

  getById: (id) => request(`/products/products/${id}`),

  create: (data) => request('/products/products', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => request(`/products/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id) => request(`/products/products/${id}`, {
    method: 'DELETE',
  }),
};

// Categories API
export const categoriesAPI = {
  getAll: () => request('/products/categories'),
  create: (data) => request('/products/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Blog API
export const blogAPI = {
  getPosts: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return request(`/blog/posts${queryString ? `?${queryString}` : ''}`);
  },
  getPost: (id) => request(`/blog/posts/${id}`),
  getPostBySlug: (slug) => request(`/blog/posts/slug/${slug}`),
  createPost: (data) => request('/blog/posts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updatePost: (id, data) => request(`/blog/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deletePost: (id) => request(`/blog/posts/${id}`, {
    method: 'DELETE',
  }),
  getCategories: () => request('/blog/categories'),
  createCategory: (data) => request('/blog/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getComments: (postId) => request(`/blog/comments/${postId}`),
  createComment: (data) => request('/blog/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// AI Query API
export const aiAPI = {
  query: (query) => request(`/services/ai/query?query=${encodeURIComponent(query)}`, {
    method: 'POST',
  }),
};

// Scraping API
export const scrapingAPI = {
  scrapeProduct: (data) => request('/services/scraping/scrape-product', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getTasks: () => request('/services/scraping/tasks'),
  getTask: (id) => request(`/services/scraping/tasks/${id}`),
};

// Export API
export const exportAPI = {
  exportExcel: (data) => request('/services/export/excel', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  exportGoogleSheets: (data) => request('/services/export/google-sheets', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  downloadFile: async (taskId) => {
    const fullUrl = `${API_BASE_URL}/services/export/download/${taskId}`;
    const response = await fetch(fullUrl);
    if (!response.ok) throw new Error('下載失敗');
    return response.blob();
  },
  getTasks: () => request('/services/export/tasks'),
};

// LOI (意向書) API
export const loiAPI = {
  // Campaign
  getCampaigns: () => request('/loi/campaigns'),
  getCampaign: (id) => request(`/loi/campaigns/${id}`),
  createCampaign: (data) => request('/loi/campaigns', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCampaign: (id, data) => request(`/loi/campaigns/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Product Import
  importProducts: async (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const fullUrl = `${API_BASE_URL}/loi/campaigns/${campaignId}/products/import`;
    const response = await fetch(fullUrl, { method: 'POST', body: formData });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      if (response.status === 402 && (error?.detail?.error === "subscription_required" || error?.error === "subscription_required")) {
        window.dispatchEvent(new CustomEvent("spo:subscription_required", { detail: error }));
      }
      throw new Error((typeof error.detail === 'string' ? error.detail : error.detail?.message || JSON.stringify(error)) || `HTTP ${response.status}`);
    }
    return { data: await response.json() };
  },
  getProducts: (campaignId, brandId) => {
    const params = brandId ? `?brand_id=${brandId}` : '';
    return request(`/loi/campaigns/${campaignId}/products${params}`);
  },

  // Dealer
  importDealers: async (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const fullUrl = `${API_BASE_URL}/loi/campaigns/${campaignId}/dealers/import`;
    const response = await fetch(fullUrl, { method: 'POST', body: formData });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      if (response.status === 402 && (error?.detail?.error === "subscription_required" || error?.error === "subscription_required")) {
        window.dispatchEvent(new CustomEvent("spo:subscription_required", { detail: error }));
      }
      throw new Error((typeof error.detail === 'string' ? error.detail : error.detail?.message || JSON.stringify(error)) || `HTTP ${response.status}`);
    }
    return { data: await response.json() };
  },
  getDealers: (campaignId, statusFilter) => {
    const params = statusFilter ? `?status_filter=${statusFilter}` : '';
    return request(`/loi/campaigns/${campaignId}/dealers${params}`);
  },
  getDealer: (campaignId, dealerId) => request(`/loi/campaigns/${campaignId}/dealers/${dealerId}`),

  // Email
  sendEmails: (campaignId, data) => request(`/loi/campaigns/${campaignId}/emails/send`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  sendReminders: (campaignId) => request(`/loi/campaigns/${campaignId}/emails/remind`, {
    method: 'POST',
  }),
  getEmailLogs: (campaignId) => request(`/loi/campaigns/${campaignId}/emails`),

  // Dashboard
  getDashboardStats: (campaignId) => request(`/loi/campaigns/${campaignId}/dashboard/stats`),
  getFunnelData: (campaignId) => request(`/loi/campaigns/${campaignId}/dashboard/funnel`),
  getBrandInterest: (campaignId) => request(`/loi/campaigns/${campaignId}/dashboard/brand-interest`),
  getDealerStatus: (campaignId, sendDate) => {
    const params = sendDate ? `?send_date=${sendDate}` : '';
    return request(`/loi/campaigns/${campaignId}/dashboard/dealer-status${params}`);
  },
  getSendDates: (campaignId) => request(`/loi/campaigns/${campaignId}/dashboard/send-dates`),

  // Survey (public)
  getSurveyData: (token) => request(`/loi/survey/${token}`),
  submitSurvey: (token, data) => request(`/loi/survey/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Tender (招標填報) API
export const tenderAPI = {
  // Public - Dealer Tender Confirm Page
  getTenderPage: (token) => request(`/tender/confirm/${token}`),
  submitTenderConfirm: (token, data) => request(`/tender/confirm/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Public - Price Catalog Page
  getPriceCatalog: (token) => request(`/tender/prices/${token}`),

  // Admin - Send Emails
  sendTenderEmails: (campaignId, data) => request(`/tender/campaigns/${campaignId}/tender/emails/send`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  sendTenderReminders: (campaignId) => request(`/tender/campaigns/${campaignId}/tender/emails/remind`, {
    method: 'POST',
  }),

  // Admin - Custom Email
  sendCustomEmail: (campaignId, data) => request(`/tender/campaigns/${campaignId}/emails/custom`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Public - Full Survey (完整意向書)
  getFullSurvey: (token) => request(`/tender/survey-full/${token}`),
  submitFullSurvey: (token, data) => request(`/tender/survey-full/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Admin - Dashboard
  getDashboardStats: (campaignId) => request(`/tender/campaigns/${campaignId}/tender/dashboard/stats`),
  getDealerStatus: (campaignId) => request(`/tender/campaigns/${campaignId}/tender/dashboard/dealers`),
  getBrandStats: (campaignId) => request(`/tender/campaigns/${campaignId}/tender/dashboard/brands`),

  // Seminar Registration
  checkSeminarStatus: (token) => request(`/tender/seminar/${token}/status`),
  registerSeminar: (token, data) => request(`/tender/seminar/${token}/register`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Admin - Seminar Registrations
  getSeminarRegistrations: (campaignId) => request(`/tender/campaigns/${campaignId}/seminar/registrations`),
  deleteSeminarRegistration: (regId) => request(`/tender/seminar/registrations/${regId}`, {
    method: 'DELETE',
  }),
};

// Analytics API
export const analyticsAPI = {
  trackVisit: (data) => request('/analytics/track', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  captureLead: (data) => request('/analytics/lead', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getSummary: () => request('/analytics/summary'),
  getVisitTrend: (days = 30) => request(`/analytics/visits/trend?days=${days}`),
  getLeads: () => request('/analytics/leads'),
  deleteLead: (id) => request(`/analytics/leads/${id}`, { method: 'DELETE' }),
};

// Auth API
export const authAPI = {
  login: (username, password) => request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }),
  register: (data) => request('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  me: () => request('/auth/me'),
  getUsers: () => request('/auth/users'),
  createUser: (data) => request('/auth/users', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateUser: (id, data) => request(`/auth/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteUser: (id) => request(`/auth/users/${id}`, {
    method: 'DELETE',
  }),
  updateMenuConfig: (id, menuConfig) => request(`/auth/users/${id}/menu-config`, {
    method: 'PUT',
    body: JSON.stringify({ menu_config: menuConfig }),
  }),
  getCustomerDashboard: (userId) => request(`/auth/users/${userId}/dashboard`),
  getPendingUsers: () => request('/auth/users/pending'),
  approveUser: (id) => request(`/auth/users/${id}/approve`, { method: 'POST' }),
  rejectUser: (id) => request(`/auth/users/${id}/reject`, { method: 'POST' }),
  getLoginLogs: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', params.limit);
    if (params.success !== undefined) q.set('success', params.success);
    if (params.username) q.set('username', params.username);
    return request(`/auth/login-logs?${q}`);
  },
  getLoginStats: (days = 30) => request(`/auth/login-logs/stats?days=${days}`),
};

// Scenario (行銷情境) API
export const scenarioAPI = {
  list: (params) => request('/scenarios?' + new URLSearchParams(params)),
  get: (id) => request(`/scenarios/${id}`),
  create: (data) => request('/scenarios', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/scenarios/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  clone: (id, data) => request(`/scenarios/${id}/clone`, { method: 'POST', body: JSON.stringify(data) }),
  delete: (id) => request(`/scenarios/${id}`, { method: 'DELETE' }),
  getSteps: (id) => request(`/scenarios/${id}/steps`),
  updateSteps: (id, data) => request(`/scenarios/${id}/steps`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStep: (id, stepId, data) => request(`/scenarios/${id}/steps/${stepId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getContents: (id) => request(`/scenarios/${id}/contents`),
  getContent: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}`),
  generate: (id, stepType, data) => request(`/scenarios/${id}/generate/${stepType}`, { method: 'POST', body: JSON.stringify(data) }),
  listContents: (id) => request(`/scenarios/${id}/contents`),
  publishContent: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}/publish`, { method: 'POST' }),
  unpublishContent: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}/unpublish`, { method: 'POST' }),
  exportPptx: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/export-pptx`, { method: 'POST', body: JSON.stringify(data || {}) }),
  pptxToWeb: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}/pptx-to-web`, { method: 'POST' }),
  // Marketing Publish
  publishToFacebook: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/publish-facebook`, { method: 'POST', body: JSON.stringify(data) }),
  publishToGoogleAds: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/publish-google-ads`, { method: 'POST', body: JSON.stringify(data) }),
  checkMarketingConfig: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}/check-marketing-config`, { method: 'POST' }),
  // Import Google Slides
  importGSlides: (scenarioId, data) => request(`/scenarios/${scenarioId}/import-gslides`, { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  // Blog Publish
  publishBlog: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/publish-blog`, { method: 'POST', body: JSON.stringify(data) }),
  getBlogPlatforms: (id, contentId) => request(`/scenarios/${id}/contents/${contentId}/blog-platforms`),
  // Publish specific version as standalone page
  publishVersion: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/publish-version`, { method: 'POST', body: JSON.stringify(data) }),
  // Email Campaign from content
  createEmailCampaign: (id, contentId, data) => request(`/scenarios/${id}/contents/${contentId}/create-email-campaign`, { method: 'POST', body: JSON.stringify(data) }),
  // Blog crawler
  blogCrawl: (id, data) => request(`/scenarios/${id}/blog-crawl`, { method: 'POST', body: JSON.stringify(data) }),
  blogCrawlList: (id) => request(`/scenarios/${id}/blog-crawl`),
  blogCrawlGenerate: (id, data) => request(`/scenarios/${id}/blog-crawl/generate`, { method: 'POST', body: JSON.stringify(data) }),
};

// Contacts (聯絡人管理) API
export const contactsAPI = {
  list: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/contacts${queryString ? `?${queryString}` : ''}`);
  },
  get: (id) => request(`/contacts/${id}`),
  create: (data) => request('/contacts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/contacts/${id}`, { method: 'DELETE' }),
  import: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const fullUrl = `${API_BASE_URL}/contacts/import`;
    const token = localStorage.getItem('spo_auth_token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, { method: 'POST', body: formData, headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      if (response.status === 402 && (error?.detail?.error === "subscription_required" || error?.error === "subscription_required")) {
        window.dispatchEvent(new CustomEvent("spo:subscription_required", { detail: error }));
      }
      throw new Error((typeof error.detail === 'string' ? error.detail : error.detail?.message || JSON.stringify(error)) || `HTTP ${response.status}`);
    }
    return { data: await response.json() };
  },
  // Contact Lists
  listLists: () => request('/contacts/lists'),
  getList: (id) => request(`/contacts/lists/${id}`),
  createList: (data) => request('/contacts/lists', { method: 'POST', body: JSON.stringify(data) }),
  addMembers: (listId, contactIds) => request(`/contacts/lists/${listId}/members`, {
    method: 'POST', body: JSON.stringify({ contact_ids: contactIds }),
  }),
  removeMember: (listId, contactId) => request(`/contacts/lists/${listId}/members/${contactId}`, { method: 'DELETE' }),
};

// Email Campaign (郵件行銷) API
export const emailCampaignAPI = {
  list: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/email-campaigns${queryString ? `?${queryString}` : ''}`);
  },
  get: (id) => request(`/email-campaigns/${id}`),
  create: (data) => request('/email-campaigns', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/email-campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  send: (id) => request(`/email-campaigns/${id}/send`, { method: 'POST' }),
  schedule: (id, data) => request(`/email-campaigns/${id}/schedule`, { method: 'POST', body: JSON.stringify(data) }),
  getStats: (id) => request(`/email-campaigns/${id}/stats`),
  getEvents: (id, params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/email-campaigns/${id}/events${queryString ? `?${queryString}` : ''}`);
  },
};

// Landing Page API
export const landingPageAPI = {
  list: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/landing-pages${queryString ? `?${queryString}` : ''}`);
  },
  get: (id) => request(`/landing-pages/${id}`),
  create: (data) => request('/landing-pages', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/landing-pages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/landing-pages/${id}`, { method: 'DELETE' }),
  getSubmissions: (id, params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/landing-pages/${id}/submissions${queryString ? `?${queryString}` : ''}`);
  },
  getStats: (id) => request(`/landing-pages/${id}/stats`),
};

// Chatbot API
export const chatbotAPI = {
  list: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/chatbot${queryString ? `?${queryString}` : ''}`);
  },
  get: (id) => request(`/chatbot/${id}`),
  create: (data) => request('/chatbot', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/chatbot/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/chatbot/${id}`, { method: 'DELETE' }),
  listSessions: (configId) => request(`/chatbot/${configId}/sessions`),
  getSession: (configId, sessionId) => request(`/chatbot/${configId}/sessions/${sessionId}`),
  getStats: (configId) => request(`/chatbot/${configId}/stats`),
  // Public
  publicChat: (token, data, sessionToken) => {
    const params = sessionToken ? `?session_token=${sessionToken}` : '';
    return request(`/chatbot/public/${token}/chat${params}`, { method: 'POST', body: JSON.stringify(data) });
  },
  publicConfig: (token) => request(`/chatbot/public/${token}/config`),
};

// Funnel Dashboard API
export const funnelAPI = {
  getSummary: () => request('/funnel/summary'),
  getFunnel: (scenarioId) => request(`/funnel/scenario/${scenarioId}`),
  getTimeSeries: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/funnel/time-series${queryString ? `?${queryString}` : ''}`);
  },
  getContactJourney: (contactId) => request(`/funnel/contact/${contactId}/journey`),
  getEvents: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/funnel/events${queryString ? `?${queryString}` : ''}`);
  },
  createEvent: (data) => request('/funnel/events', { method: 'POST', body: JSON.stringify(data) }),
};

// Google Drive API
export const gdriveAPI = {
  listFolders: (parentId) => {
    const params = parentId ? `?parent_id=${encodeURIComponent(parentId)}` : '';
    return request(`/gdrive/folders${params}`);
  },
  listFiles: (folderId) => request(`/gdrive/files?folder_id=${encodeURIComponent(folderId)}`),
  browse: (folderId) => {
    const params = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '';
    return request(`/gdrive/browse${params}`);
  },
  getFileContent: (fileId, fileName) => {
    const params = new URLSearchParams({ file_id: fileId });
    if (fileName) params.append('file_name', fileName);
    return request(`/gdrive/file-content?${params}`);
  },
};

// System Config (AI Model Management) API
export const systemConfigAPI = {
  list: () => request('/system-config'),
  update: (data) => request('/system-config', { method: 'PUT', body: JSON.stringify(data) }),
  getRaw: (key) => request(`/system-config/raw/${key}`),
  testClaude: () => request('/system-config/test-claude', { method: 'POST' }),
  testOllama: () => request('/system-config/test-ollama', { method: 'POST' }),
  testOpenai: () => request('/system-config/test-openai', { method: 'POST' }),
  testGemini: () => request('/system-config/test-gemini', { method: 'POST' }),
  testDeepseek: () => request("/system-config/test-deepseek", { method: "POST" }),
};

// Google Ads Integration Hub API
export const googleAdsAPI = {
  getStatus: () => request('/google-ads/status'),
  getIntegrationSummary: () => request('/google-ads/integration-summary'),
  getPerformance: (dateRange = 'LAST_30_DAYS') => request(`/google-ads/performance?date_range=${dateRange}`),
  listCampaigns: () => request('/google-ads/campaigns'),
  saveConfig: (data) => request('/google-ads/save-config', { method: 'POST', body: JSON.stringify(data) }),
  aiGenerateCopy: (data) => request('/google-ads/ai-generate-copy', { method: 'POST', body: JSON.stringify(data) }),
  createFromLandingPage: (data) => request('/google-ads/create-from-landing-page', { method: 'POST', body: JSON.stringify(data) }),
  createFromEmailCampaign: (data) => request('/google-ads/create-from-email-campaign', { method: 'POST', body: JSON.stringify(data) }),
  createFromContactList: (data) => request('/google-ads/create-from-contact-list', { method: 'POST', body: JSON.stringify(data) }),
  createFromScenario: (data) => request('/google-ads/create-from-scenario', { method: 'POST', body: JSON.stringify(data) }),
  quickCreate: (sourceType, sourceId) => request(`/google-ads/quick-create?source_type=${sourceType}&source_id=${sourceId}`, { method: 'POST' }),
};

// Video Generation (Wav2Lip) API
export const videoAPI = {
  getSourceVideos: (params) => {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return request(`/video/source-videos${queryString ? `?${queryString}` : ''}`);
  },
  getSourceVideo: (id) => request(`/video/source-videos/${id}`),
  getCategories: () => request(`/video/categories`),
  getVoices: () => request(`/video/voices`),
  health: () => request(`/video/health`),
  getThumbnailUrl: (id) => `${API_BASE_URL}/video/source-videos/${id}/thumbnail`,
  getPreviewUrl: (id) => `${API_BASE_URL}/video/source-videos/${id}/preview`,
  // Start async video generation — returns { job_id }
  createVideo: (data) => request('/video/create', { method: 'POST', body: JSON.stringify(data) }),
  // Poll job status
  getJobStatus: (jobId) => request(`/video/jobs/${jobId}`),
  // Helper: create video and poll until done (returns final result)
  createVideoAndWait: async (data, onProgress) => {
    const startRes = await request('/video/create', { method: 'POST', body: JSON.stringify(data) });
    const jobId = startRes?.data?.job_id;
    if (!jobId) throw new Error(startRes?.data?.error || '無法啟動影片生成');

    // Poll every 3 seconds
    while (true) {
      await new Promise(r => setTimeout(r, 3000));
      const pollRes = await request(`/video/jobs/${jobId}`);
      const job = pollRes?.data;
      if (!job) throw new Error('無法取得任務狀態');

      if (onProgress && job.elapsed_ms) onProgress(job.elapsed_ms);

      if (job.status === 'completed') return { data: job };
      if (job.status === 'failed') return { data: job };
    }
  },
  postVideoToFacebook: (data) => request('/video/post-to-facebook', { method: 'POST', body: JSON.stringify(data) }),
  listGenerated: () => request('/video/generated'),
  deleteGenerated: (filename) => request(`/video/generated/${filename}`, { method: 'DELETE' }),
};

// Facebook Integration Hub API
export const facebookAPI = {
  getStatus: () => request('/facebook/status'),
  getIntegrationSummary: () => request('/facebook/integration-summary'),
  saveConfig: (data) => request('/facebook/save-config', { method: 'POST', body: JSON.stringify(data) }),
  aiGenerateCopy: (data) => request('/facebook/ai-generate-copy', { method: 'POST', body: JSON.stringify(data) }),
  createFromLandingPage: (data) => request('/facebook/create-from-landing-page', { method: 'POST', body: JSON.stringify(data) }),
  createFromEmailCampaign: (data) => request('/facebook/create-from-email-campaign', { method: 'POST', body: JSON.stringify(data) }),
  createFromContactList: (data) => request('/facebook/create-from-contact-list', { method: 'POST', body: JSON.stringify(data) }),
  createFromScenario: (data) => request('/facebook/create-from-scenario', { method: 'POST', body: JSON.stringify(data) }),
  crossPlatformCampaign: (data) => request('/facebook/cross-platform-campaign', { method: 'POST', body: JSON.stringify(data) }),
  postBlog: (data) => request('/facebook/post-blog', { method: 'POST', body: JSON.stringify(data) }),
  directPost: (data) => request('/facebook/direct-post', { method: 'POST', body: JSON.stringify(data) }),
  createFromProperty: (data) => request('/facebook/create-from-property', { method: 'POST', body: JSON.stringify(data) }),
  // Devdoc new functions
  getPosts: (limit = 10) => request(`/facebook/posts?limit=${limit}`),
  getPostStatus: (postId) => request(`/facebook/posts/${encodeURIComponent(postId)}`),
  getTokenStatus: () => request('/facebook/token-status'),
};

// Mira Bot Chat API
export const miraAPI = {
  chat: (data) => request('/mira/chat', { method: 'POST', body: JSON.stringify(data) }),
  status: () => request('/mira/status'),
  history: () => request('/mira/history'),
  clearHistory: () => request('/mira/history', { method: 'DELETE' }),
};

export const marketingSiteAPI = {
  deploy: (scenarioId, data = {}) => request(`/marketing-sites/deploy/${scenarioId}`, { method: 'POST', body: JSON.stringify(data) }),
  list: (params) => request('/marketing-sites?' + new URLSearchParams(params || {})),
  get: (id) => request(`/marketing-sites/${id}`),
  update: (id, data) => request(`/marketing-sites/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateSections: (id, data) => request(`/marketing-sites/${id}/sections`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/marketing-sites/${id}`, { method: 'DELETE' }),
  refresh: (id) => request(`/marketing-sites/${id}/refresh`, { method: 'POST' }),
  getSiteConfig: (id) => request(`/marketing-sites/${id}/site-config`),
  updateSiteConfig: (id, data) => request(`/marketing-sites/${id}/site-config`, { method: 'PUT', body: JSON.stringify(data) }),
  getFrontendSections: (id) => request(`/marketing-sites/${id}/frontend-sections`),
  updateFrontendSections: (id, data) => request(`/marketing-sites/${id}/frontend-sections`, { method: 'PUT', body: JSON.stringify(data) }),
};

export const aeoScannerAPI = {
  scan: (data) => request('/aeo-scanner/scan', { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  list: (params) => request('/aeo-scanner/scans?' + new URLSearchParams(params || {})),
  get: (id) => request(`/aeo-scanner/scans/${id}`),
  delete: (id) => request(`/aeo-scanner/scans/${id}`, { method: 'DELETE' }),
  domainHistory: (domain) => request(`/aeo-scanner/scans/domain/${encodeURIComponent(domain)}`),
  exportReport: (id) => {
    const token = localStorage.getItem('spo_auth_token');
    return fetch(`${API_BASE_URL}/aeo-scanner/scans/${id}/report`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    }).then(res => {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    });
  },
};

export const citationMonitorAPI = {
  // Brands
  listBrands: () => request('/citation-monitor/brands'),
  getBrand: (id) => request(`/citation-monitor/brands/${id}`),
  createBrand: (data) => request('/citation-monitor/brands', { method: 'POST', body: JSON.stringify(data) }),
  updateBrand: (id, data) => request(`/citation-monitor/brands/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBrand: (id) => request(`/citation-monitor/brands/${id}`, { method: 'DELETE' }),
  // Keywords
  addKeyword: (brandId, data) => request(`/citation-monitor/brands/${brandId}/keywords`, { method: 'POST', body: JSON.stringify(data) }),
  updateKeyword: (keywordId, data) => request(`/citation-monitor/keywords/${keywordId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteKeyword: (keywordId) => request(`/citation-monitor/keywords/${keywordId}`, { method: 'DELETE' }),
  // Whitelist
  addWhitelist: (brandId, data) => request(`/citation-monitor/brands/${brandId}/whitelist`, { method: 'POST', body: JSON.stringify(data) }),
  deleteWhitelist: (whitelistId) => request(`/citation-monitor/whitelist/${whitelistId}`, { method: 'DELETE' }),
  // Monitor
  runMonitor: (brandId, data) => request(`/citation-monitor/brands/${brandId}/monitor/run`, { method: 'POST', body: JSON.stringify(data || {}), timeout: 300000 }),
  // Queries
  listQueries: (brandId, params) => request(`/citation-monitor/brands/${brandId}/queries?` + new URLSearchParams(params || {})),
  getQuery: (queryId) => request(`/citation-monitor/queries/${queryId}`),
  // Dashboard
  getDashboard: (brandId) => request(`/citation-monitor/brands/${brandId}/dashboard`),
  // Reports
  generateReport: (brandId, data) => request(`/citation-monitor/brands/${brandId}/reports`, { method: 'POST', body: JSON.stringify(data), timeout: 300000 }),
  listReports: (brandId) => request(`/citation-monitor/brands/${brandId}/reports`),
  getReport: (reportId) => request(`/citation-monitor/reports/${reportId}`),
  downloadReport: (reportId) => {
    const token = localStorage.getItem('spo_auth_token');
    const url = `${API_BASE_URL}/citation-monitor/reports/${reportId}/download?token=${encodeURIComponent(token || '')}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `AI_Citation_Report_${reportId}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
  // Feature 1: Auto-schedule settings
  updateSchedule: (brandId, data) => request(`/citation-monitor/brands/${brandId}/schedule`, { method: 'PUT', body: JSON.stringify(data) }),
  // Feature 3: Stability History
  getStabilityHistory: (brandId) => request(`/citation-monitor/brands/${brandId}/stability-history`),
  // Feature 4: llms.txt Generator
  generateLlmsTxt: (brandId, data) => request(`/citation-monitor/brands/${brandId}/generate-llms-txt`, { method: 'POST', body: JSON.stringify(data), timeout: 60000 }),
  // Feature 5: Schema Markup Generator
  generateSchema: (brandId, data) => request(`/citation-monitor/brands/${brandId}/generate-schema`, { method: 'POST', body: JSON.stringify(data), timeout: 60000 }),
  // Feature 6: AIO Analysis
  aioAnalysis: (brandId, data) => request(`/citation-monitor/brands/${brandId}/aio-analysis`, { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  getLatestAio: (brandId) => request(`/citation-monitor/brands/${brandId}/aio-analysis`),
  // Feature 7: Brand Persona
  generatePersona: (brandId, data) => request(`/citation-monitor/brands/${brandId}/generate-persona`, { method: 'POST', body: JSON.stringify(data), timeout: 60000 }),
  getLatestPersona: (brandId) => request(`/citation-monitor/brands/${brandId}/persona`),
  // Feature 8: GEO Lite
  generateGeoLite: (brandId, data) => request(`/citation-monitor/brands/${brandId}/geo-lite/generate`, { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  getGeoLite: (brandId) => request(`/citation-monitor/brands/${brandId}/geo-lite`),
  // Feature 9: GEO Content
  generateGeoContent: (brandId, data) => request(`/citation-monitor/brands/${brandId}/geo-content/generate`, { method: 'POST', body: JSON.stringify(data), timeout: 180000 }),
  listGeoContent: (brandId) => request(`/citation-monitor/brands/${brandId}/geo-content`),
  deleteGeoContent: (contentId) => request(`/citation-monitor/geo-content/${contentId}`, { method: 'DELETE' }),
  // Feature 10: FAQ Knowledge Base
  listFaqs: (brandId) => request(`/citation-monitor/brands/${brandId}/faqs`),
  createFaq: (brandId, data) => request(`/citation-monitor/brands/${brandId}/faqs`, { method: 'POST', body: JSON.stringify(data) }),
  updateFaq: (faqId, data) => request(`/citation-monitor/faqs/${faqId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFaq: (faqId) => request(`/citation-monitor/faqs/${faqId}`, { method: 'DELETE' }),
  generateFaqs: (brandId, data) => request(`/citation-monitor/brands/${brandId}/faqs/generate`, { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  getFaqJsonLd: (brandId) => request(`/citation-monitor/brands/${brandId}/faqs/json-ld`),
};

// Billing & Token Management API
export const billingAPI = {
  // Coupon & Subscription
  redeemCoupon: (couponCode, customerToken) => request('/billing/coupon/redeem', { method: 'POST', body: JSON.stringify({ coupon_code: couponCode, customer_token: customerToken }) }),
  getSubscriptionStatus: () => request('/billing/subscription-status'),

  // Config (admin)
  getConfig: () => request('/billing/config'),
  updateConfig: (data) => request('/billing/config', { method: 'PUT', body: JSON.stringify(data) }),
  // Plans
  listPlans: () => request('/billing/plans'),
  listAllPlans: () => request('/billing/plans/all'),
  createPlan: (data) => request('/billing/plans', { method: 'POST', body: JSON.stringify(data) }),
  updatePlan: (planId, data) => request(`/billing/plans/${planId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePlan: (planId) => request(`/billing/plans/${planId}`, { method: 'DELETE' }),
  // Status & Terms
  getStatus: () => request('/billing/status'),
  acceptTerms: () => request('/billing/accept-terms', { method: 'POST' }),
  // Balance
  getBalance: () => request('/billing/balance'),
  getAllBalances: () => request('/billing/balances'),
  // Subscription
  subscribe: (planId) => request('/billing/subscribe', { method: 'POST', body: JSON.stringify({ plan_id: planId }) }),
  updateAutoTopup: (data) => request('/billing/auto-topup', { method: 'PUT', body: JSON.stringify(data) }),
  // Admin
  getPending: () => request('/billing/pending'),
  approveTransaction: (id, action) => request(`/billing/approve/${id}`, { method: 'POST', body: JSON.stringify({ action }) }),
  grantCredits: (data) => request('/billing/grant', { method: 'POST', body: JSON.stringify(data) }),
  // Transactions
  getTransactions: (params) => {
    const q = params ? new URLSearchParams(params).toString() : '';
    return request(`/billing/transactions${q ? `?${q}` : ''}`);
  },
  getAllTransactions: (params) => {
    const q = params ? new URLSearchParams(params).toString() : '';
    return request(`/billing/transactions/all${q ? `?${q}` : ''}`);
  },
  // Usage
  getUsage: (params) => {
    const q = params ? new URLSearchParams(params).toString() : '';
    return request(`/billing/usage${q ? `?${q}` : ''}`);
  },
  getAllUsage: (params) => {
    const q = params ? new URLSearchParams(params).toString() : '';
    return request(`/billing/usage/all${q ? `?${q}` : ''}`);
  },
  getUsageSummary: () => request('/billing/usage/summary'),
  // Invoices
  getInvoices: () => request('/billing/invoices'),
  getAllInvoices: (params) => {
    const q = params ? new URLSearchParams(params).toString() : '';
    return request(`/billing/invoices/all${q ? `?${q}` : ''}`);
  },
  updateInvoice: (id, data) => request(`/billing/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
};

export const businessModuleAPI = {
  getMap: () => request('/business-modules/map'),
  updateModule: (moduleKey, data) => request(`/business-modules/modules/${moduleKey}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// ── Scenario Site (Admin) ───────────────────────────────
export const scenarioSiteAdminAPI = {
  getPublishedSites: () => request('/published-sites'),
  getSiteConfig: (scenarioId) => request(`/scenarios/${scenarioId}/site-config`),
  createSiteConfig: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-config`, { method: 'POST', body: JSON.stringify(data) }),
  updateSiteConfig: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-config`, { method: 'PUT', body: JSON.stringify(data) }),
  // Products
  listProducts: (scenarioId) => request(`/scenarios/${scenarioId}/site-products`),
  addProduct: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-products`, { method: 'POST', body: JSON.stringify(data) }),
  updateProduct: (scenarioId, spId, data) => request(`/scenarios/${scenarioId}/site-products/${spId}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeProduct: (scenarioId, spId) => request(`/scenarios/${scenarioId}/site-products/${spId}`, { method: 'DELETE' }),
  createProductDirect: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-products/direct`, { method: 'POST', body: JSON.stringify(data) }),
  updateProductDirect: (scenarioId, spId, data) => request(`/scenarios/${scenarioId}/site-products/${spId}/direct`, { method: 'PUT', body: JSON.stringify(data) }),
  reorderProducts: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-products/reorder`, { method: 'POST', body: JSON.stringify(data) }),
  // Events
  listEvents: (scenarioId) => request(`/scenarios/${scenarioId}/events`),
  createEvent: (scenarioId, data) => request(`/scenarios/${scenarioId}/events`, { method: 'POST', body: JSON.stringify(data) }),
  updateEvent: (scenarioId, eventId, data) => request(`/scenarios/${scenarioId}/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEvent: (scenarioId, eventId) => request(`/scenarios/${scenarioId}/events/${eventId}`, { method: 'DELETE' }),
  listRegistrations: (scenarioId, eventId) => request(`/scenarios/${scenarioId}/events/${eventId}/registrations`),
  // Inquiries
  listInquiries: (scenarioId) => request(`/scenarios/${scenarioId}/inquiries`),
  // Site Email
  getSiteRecipients: (scenarioId) => request(`/scenarios/${scenarioId}/site-recipients`),
  sendSiteEmail: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-email/send`, { method: 'POST', body: JSON.stringify(data) }),
  getSiteEmailHistory: (scenarioId) => request(`/scenarios/${scenarioId}/site-email/history`),
  // Site Sections
  listSections: (scenarioId) => request(`/scenarios/${scenarioId}/site-sections`),
  createSection: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-sections`, { method: 'POST', body: JSON.stringify(data) }),
  updateSection: (scenarioId, sectionId, data) => request(`/scenarios/${scenarioId}/site-sections/${sectionId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSection: (scenarioId, sectionId) => request(`/scenarios/${scenarioId}/site-sections/${sectionId}`, { method: 'DELETE' }),
  reorderSections: (scenarioId, data) => request(`/scenarios/${scenarioId}/site-sections/reorder`, { method: 'POST', body: JSON.stringify(data) }),
  autoGenerateSite: (scenarioId) => request(`/scenarios/${scenarioId}/auto-generate-site`, { method: 'POST' }),
  translateContent: (scenarioId, contentId, data) => request(`/scenarios/${scenarioId}/contents/${contentId}/translate`, { method: 'POST', body: JSON.stringify(data) }),
  uploadFile: (scenarioId, file, category = 'site') => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('spo_auth_token');
    return fetch(`${API_BASE_URL}/scenarios/${scenarioId}/site-upload?category=${category}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    }).then(async res => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      return { data };
    });
  },
};

// ── Scenario Site (Public — no auth) ────────────────────
const getPublicBaseUrl = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  if (!port || port === '80' || port === '443') {
    return `${window.location.protocol}//${hostname}/s`;
  }
  if (port === '3000') {
    return `http://${hostname}:8000/s`;
  }
  return `http://${hostname}:${port}/s`;
};

async function publicRequest(url, options = {}) {
  const fullUrl = `${getPublicBaseUrl()}${url}`;
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const response = await fetch(fullUrl, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
      if (response.status === 402 && (error?.detail?.error === "subscription_required" || error?.error === "subscription_required")) {
        window.dispatchEvent(new CustomEvent("spo:subscription_required", { detail: error }));
      }
    throw new Error((typeof error.detail === 'string' ? error.detail : error.detail?.message || JSON.stringify(error)) || `HTTP ${response.status}`);
  }
  const data = await response.json();
  return { data };
}

export const scenarioSiteAPI = {
  getInfo: (slug) => publicRequest(`/${slug}/info`),
  getProducts: (slug, lang) => publicRequest(`/${slug}/products${lang ? `?lang=${lang}` : ''}`),
  getProduct: (slug, productId, lang) => publicRequest(`/${slug}/products/${productId}${lang ? `?lang=${lang}` : ''}`),
  getEvents: (slug, lang) => publicRequest(`/${slug}/events${lang ? `?lang=${lang}` : ''}`),
  registerEvent: (slug, eventId, data) => publicRequest(`/${slug}/events/${eventId}/register`, { method: 'POST', body: JSON.stringify(data) }),
  submitInquiry: (slug, data) => publicRequest(`/${slug}/inquiry`, { method: 'POST', body: JSON.stringify(data) }),
  getSections: (slug, lang) => publicRequest(`/${slug}/sections${lang ? `?lang=${lang}` : ''}`),
};

// LINE Bot API
export const lineAPI = {
  listAccounts: () => request('/line/accounts'),
  addAccount: (data) => request('/line/accounts', { method: 'POST', body: JSON.stringify(data) }),
  updateAccount: (id, data) => request(`/line/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAccount: (id) => request(`/line/accounts/${id}`, { method: 'DELETE' }),
  refreshAccount: (id) => request(`/line/accounts/${id}/refresh`, { method: 'POST' }),
  toggleGroupReply: (id, enabled) => request(`/line/accounts/${id}/group-reply?enabled=${enabled}`, { method: 'PUT' }),
  sendMessage: (id, data) => request(`/line/accounts/${id}/send`, { method: 'POST', body: JSON.stringify(data) }),
  getMessages: (id, sourceType) => request(`/line/accounts/${id}/messages${sourceType ? `?source_type=${sourceType}` : ''}`),
};

// EMBot (Email Marketing Bot) API
export const embotAPI = {
  chat: (data) => request('/embot/chat', { method: 'POST', body: JSON.stringify(data) }),
  history: () => request('/embot/history'),
  clearHistory: () => request('/embot/history', { method: 'DELETE' }),
  send: (data) => request('/embot/send', { method: 'POST', body: JSON.stringify(data) }),
  inbox: (data) => request('/embot/inbox', { method: 'POST', body: JSON.stringify(data) }),
  readEmail: (data) => request('/embot/read', { method: 'POST', body: JSON.stringify(data) }),
  unreadSummary: () => request('/embot/unread-summary'),
  listContacts: (params) => {
    const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v != null));
    return request('/embot/contacts?' + new URLSearchParams(clean));
  },
  createContact: (data) => request('/embot/contacts', { method: 'POST', body: JSON.stringify(data) }),
  getContact: (id) => request(`/embot/contacts/${id}`),
  updateContact: (id, data) => request(`/embot/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContact: (id) => request(`/embot/contacts/${id}`, { method: 'DELETE' }),
  importContacts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('spo_auth_token');
    return fetch(`${API_BASE_URL}/embot/contacts/import`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    }).then(async res => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      return { data };
    });
  },
  listCampaigns: () => request('/embot/campaigns'),
  createCampaign: (data) => request('/embot/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  getCampaign: (id) => request(`/embot/campaigns/${id}`),
  updateCampaign: (id, data) => request(`/embot/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCampaign: (id) => request(`/embot/campaigns/${id}`, { method: 'DELETE' }),
  sendCampaign: (id) => request(`/embot/campaigns/${id}/send`, { method: 'POST' }),
  campaignStats: (id) => request(`/embot/campaigns/${id}/stats`),
  getStats: () => request('/embot/stats'),
  listTemplates: () => request('/embot/templates'),
  createTemplate: (data) => request('/embot/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id, data) => request(`/embot/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTemplate: (id) => request(`/embot/templates/${id}`, { method: 'DELETE' }),
  syncRole: () => request('/embot/role/sync', { method: 'POST' }),
};

export const schedulerAPI = {
  list: (params) => request('/marketing-scheduler/schedules?' + new URLSearchParams(params || {})),
  create: (data) => request('/marketing-scheduler/schedules', { method: 'POST', body: JSON.stringify(data) }),
  get: (id) => request(`/marketing-scheduler/schedules/${id}`),
  update: (id, data) => request(`/marketing-scheduler/schedules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/marketing-scheduler/schedules/${id}`, { method: 'DELETE' }),
  execute: (id) => request(`/marketing-scheduler/schedules/${id}/execute`, { method: 'POST' }),
  history: (id, limit) => request(`/marketing-scheduler/schedules/${id}/history?limit=${limit || 20}`),
  aiSuggest: (data) => request('/marketing-scheduler/ai-suggest', { method: 'POST', body: JSON.stringify(data) }),
  logs: (params) => request('/marketing-scheduler/logs?' + new URLSearchParams(params || {})),
  running: () => request('/marketing-scheduler/running'),
  getExecution: (id) => request(`/marketing-scheduler/executions/${id}`),
};

export const aiSalesAPI = {
  dashboard: (params) => request('/ai-sales/dashboard?' + new URLSearchParams(params || {})),
  leaderboard: (params) => request('/ai-sales/leaderboard?' + new URLSearchParams(params || {})),
  listCompetitions: () => request('/ai-sales/competitions'),
  createCompetition: (data) => request('/ai-sales/competitions', { method: 'POST', body: JSON.stringify(data) }),
  getCompetition: (id) => request(`/ai-sales/competitions/${id}`),
  updateCompetition: (id, data) => request(`/ai-sales/competitions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCompetition: (id) => request(`/ai-sales/competitions/${id}`, { method: 'DELETE' }),
  listBots: (params) => request('/ai-sales/bots?' + new URLSearchParams(params || {})),
  createBot: (data) => request('/ai-sales/bots', { method: 'POST', body: JSON.stringify(data) }),
  getBot: (id) => request(`/ai-sales/bots/${id}`),
  updateBot: (id, data) => request(`/ai-sales/bots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBot: (id) => request(`/ai-sales/bots/${id}`, { method: 'DELETE' }),
  botAction: (id, data) => request(`/ai-sales/bots/${id}/action`, { method: 'POST', body: JSON.stringify(data) }),
  botActivities: (id, limit) => request(`/ai-sales/bots/${id}/activities?limit=${limit || 50}`),
  botDashboard: (id, params) => request(`/ai-sales/bots/${id}/dashboard?` + new URLSearchParams(params || {})),
  liveOperations: (params) => request('/ai-sales/live-operations?' + new URLSearchParams(params || {})),
  listAlayaessAgents: () => request('/ai-sales/alayaess-agents'),
  listAlayaessBindings: () => request('/ai-sales/alayaess-bindings'),
  alayaessAgentUsage: () => request('/ai-sales/alayaess-agent-usage'),
  getBotChatHistory: (id, limit) => request(`/ai-sales/bots/${id}/chat?limit=${limit || 100}`),
  chatWithBot: (id, message) => request(`/ai-sales/bots/${id}/chat`, { method: 'POST', body: JSON.stringify({ message }) }),
  clearBotChatHistory: (id) => request(`/ai-sales/bots/${id}/chat`, { method: 'DELETE' }),
  listProposals: (status) => request(`/ai-sales/proposals${status ? `?status=${status}` : ''}`),
  approveProposal: (id) => request(`/ai-sales/proposals/${id}/approve`, { method: 'POST' }),
  rejectProposal: (id) => request(`/ai-sales/proposals/${id}/reject`, { method: 'POST' }),
  deleteProposal: (id) => request(`/ai-sales/proposals/${id}`, { method: 'DELETE' }),
  manualTickBot: (id) => request(`/ai-sales/bots/${id}/tick`, { method: 'POST' }),
  updateAlayaessBinding: (kind, id, data) => request(`/ai-sales/alayaess-bindings/${kind}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// ── Trends Research (last30days-skill) ──
export const trendsAPI = {
  health: () => request('/research/health'),
  search: (data) => request('/research/trends/search', { method: 'POST', body: JSON.stringify(data) }),
  listReports: (params) => request('/research/trends/reports?' + new URLSearchParams(params || {})),
  getReport: (id) => request(`/research/trends/reports/${id}`),
  deleteReport: (id) => request(`/research/trends/reports/${id}`, { method: 'DELETE' }),
  retryReport: (id) => request(`/research/trends/reports/${id}/retry`, { method: 'POST' }),
};

// ── Partner Portal (Admin) ─────────────────────────────
export const partnerPortalAPI = {
  list: () => request('/partner-portals'),
  create: (data) => request('/partner-portals', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/partner-portals/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => request(`/partner-portals/${id}`, { method: 'DELETE' }),
  reset: (id) => request(`/partner-portals/${id}/reset`, { method: 'POST' }),
};

export const partnershipAPI = {
  listInquiries: () => request('/partnership/inquiries'),
  updateInquiry: (id, data) => request(`/partnership/inquiries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInquiry: (id) => request(`/partnership/inquiries/${id}`, { method: 'DELETE' }),
};

// Backup Management API (Admin only)
export const backupAPI = {
  list: () => request('/system/backups'),
  create: (data) => request('/system/backups/create', { method: 'POST', body: JSON.stringify(data) }),
  restore: (filename) => request('/system/backups/restore', { method: 'POST', body: JSON.stringify({ filename }) }),
  remove: (filename) => request(`/system/backups/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
  getSchedule: () => request('/system/backups/schedule'),
  updateSchedule: (data) => request('/system/backups/schedule', { method: 'PUT', body: JSON.stringify(data) }),
  getSSHConfig: () => request('/system/backups/ssh-config'),
  saveSSHConfig: (data) => request('/system/backups/ssh-config', { method: 'PUT', body: JSON.stringify(data) }),
  testSSH: () => request('/system/backups/ssh-test', { method: 'POST' }),
  pushBackup: (filename) => filename
    ? request(`/system/backups/${encodeURIComponent(filename)}/push`, { method: 'POST' })
    : request('/system/backups/push-latest', { method: 'POST' }),
  tmuxPush: () => request('/system/backups/tmux-push', { method: 'POST' }),
  restoreRemote: (data) => request('/system/backups/restore-remote', { method: 'POST', body: JSON.stringify(data) }),
  restoreRemoteStatus: (jobId) => request(`/system/backups/restore-remote/status/${jobId}`),
  getGitLabLog: () => request('/system/backups/gitlab-log'),
  getGitLabConfig: () => request('/system/backups/gitlab-config'),
  saveGitLabConfig: (data) => request('/system/backups/gitlab-config', { method: 'PUT', body: JSON.stringify(data) }),
  pushToGitLab: (filename) => request('/system/backups/push-gitlab', { method: 'POST', body: JSON.stringify({ filename: filename || '' }) }),
};

export const devRequestsAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.type) q.set('type', params.type);
    if (params.priority) q.set('priority', params.priority);
    if (params.q) q.set('q', params.q);
    if (params.limit) q.set('limit', params.limit);
    if (params.offset) q.set('offset', params.offset);
    return request(`/dev-requests?${q}`);
  },
  create: (data) => request('/dev-requests', { method: 'POST', body: JSON.stringify(data) }),
  get: (id) => request(`/dev-requests/${id}`),
  update: (id, data) => request(`/dev-requests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/dev-requests/${id}`, { method: 'DELETE' }),
  addComment: (id, content) => request(`/dev-requests/${id}/comments`, { method: 'POST', body: JSON.stringify({ content }) }),
  stats: () => request('/dev-requests/stats'),
};

// AEO Auto-Fix API
export const aeoFixAPI = {
  // Targets
  listTargets: () => request('/aeo/fix/targets'),
  createTarget: (data) => request('/aeo/fix/targets', { method: 'POST', body: JSON.stringify(data) }),
  updateTarget: (id, data) => request(`/aeo/fix/targets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTarget: (id) => request(`/aeo/fix/targets/${id}`, { method: 'DELETE' }),
  // Backups
  startBackup: (targetId) => request(`/aeo/fix/targets/${targetId}/backup`, { method: 'POST' }),
  listBackups: (targetId) => request(`/aeo/fix/targets/${targetId}/backups`),
  getBackup: (id) => request(`/aeo/fix/backups/${id}`),
  listAllBackups: (params = {}) => {
    const q = new URLSearchParams();
    if (params.target_id) q.set('target_id', params.target_id);
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', params.limit);
    return request(`/aeo/fix/backups?${q}`);
  },
  // Jobs
  listJobs: (scanId) => {
    const q = scanId ? `?scan_id=${scanId}` : '';
    return request(`/aeo/fix/jobs${q}`);
  },
  createJob: (data) => request('/aeo/fix/jobs', { method: 'POST', body: JSON.stringify(data) }),
  getJob: (id) => request(`/aeo/fix/jobs/${id}`),
  rescan: (id) => request(`/aeo/fix/jobs/${id}/rescan`, { method: 'POST' }),
};

// Dev Spec API
export const devSpecAPI = {
  getCatalog: () => request('/dev-specs/catalog'),
  list: () => request('/dev-specs'),
  get: (id) => request(`/dev-specs/${id}`),
  generate: (data) => request('/dev-specs/generate', { method: 'POST', body: JSON.stringify(data), timeout: 120000 }),
  update: (id, data) => request(`/dev-specs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/dev-specs/${id}`, { method: 'DELETE' }),
};


export const couponRequestAPI = {
  apply: (notes) => request('/coupon-requests/apply', { method: 'POST', body: JSON.stringify({ notes }) }),
  myRequests: () => request('/coupon-requests/my-requests'),
  myStats: () => request('/coupon-requests/stats'),
  listAll: (status) => {
    const q = status ? "?status=" + status : '';
    return request('/coupon-requests/all' + q);
  },
  listPending: () => request('/coupon-requests/pending'),
  approve: (id, maxScans, adminNotes) => request('/coupon-requests/' + id + '/approve', {
    method: 'POST', body: JSON.stringify({ max_scans: maxScans, admin_notes: adminNotes }),
  }),
  reject: (id, adminNotes) => request('/coupon-requests/' + id + '/reject', {
    method: 'POST', body: JSON.stringify({ admin_notes: adminNotes }),
  }),
};


export const officeAPI = {
  getSources: () => request('/house/office/sources'),
  getLineInfo: () => request('/house/office/line-info'),
  getAreas: (source) => request(`/house/office/areas${source ? `?source=${encodeURIComponent(source)}` : ''}`),
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    const qs = q.toString();
    return request(`/house/office/list${qs ? `?${qs}` : ''}`);
  },
  import: (body = {}) => request('/house/office/import', { method: 'POST', body: JSON.stringify(body) }),
  similar: (propId, limit = 6) => request('/house/office/similar?prop_id=' + propId + '&limit=' + limit),
  rate: (propId, rating) => request('/house/office/' + propId + '/rating', { method: 'POST', body: JSON.stringify({ rating }) }),

  // ── Office Rental CRM (Case Manager) ──
  crmDashboard: () => request('/house/office/crm/dashboard'),
  crmLeads: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    const qs = q.toString();
    return request(`/house/office/crm/leads${qs ? `?${qs}` : ''}`);
  },
  crmCreateLead: (body) => request('/house/office/crm/leads', { method: 'POST', body: JSON.stringify(body) }),
  crmGetLead: (id) => request(`/house/office/crm/leads/${id}`),
  crmUpdateLead: (id, body) => request(`/house/office/crm/leads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  crmDeleteLead: (id) => request(`/house/office/crm/leads/${id}`, { method: 'DELETE' }),
  crmLinkProperty: (id, propertyId, status = 'interested') => request(`/house/office/crm/leads/${id}/properties?property_id=${propertyId}&status=${status}`, { method: 'POST' }),
  crmUnlinkProperty: (id, propertyId) => request(`/house/office/crm/leads/${id}/properties/${propertyId}`, { method: 'DELETE' }),
  crmAddActivity: (id, body) => request(`/house/office/crm/leads/${id}/activities`, { method: 'POST', body: JSON.stringify(body) }),
  crmStartNegotiation: (id, body) => request(`/house/office/crm/leads/${id}/negotiations`, { method: 'POST', body: JSON.stringify(body) }),
  crmNegotiationOffer: (negId, offerPrice) => request(`/house/office/crm/negotiations/${negId}/offer?offer_price=${offerPrice}`, { method: 'POST' }),
  crmNegotiationAgree: (negId, finalPrice) => request(`/house/office/crm/negotiations/${negId}/agree?final_price=${finalPrice}`, { method: 'POST' }),
  crmCloseDeal: (id, body) => request(`/house/office/crm/leads/${id}/close`, { method: 'POST', body: JSON.stringify(body) }),
};


export const homeAPI = {
  getSources: () => request('/house/home/sources'),
  getLineInfo: () => request('/house/home/line-info'),
  getAreas: (source) => request(`/house/home/areas${source ? `?source=${encodeURIComponent(source)}` : ''}`),
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    const qs = q.toString();
    return request(`/house/home/list${qs ? `?${qs}` : ''}`);
  },
  import: (body = {}) => request('/house/home/import', { method: 'POST', body: JSON.stringify(body) }),
  similar: (propId, limit = 6) => request('/house/home/similar?prop_id=' + propId + '&limit=' + limit),
  rate: (propId, rating) => request('/house/home/' + propId + '/rating', { method: 'POST', body: JSON.stringify({ rating }) }),
};

export const hotelAPI = {
  getSources: () => request('/house/hotels/sources'),
  getAreas: () => request('/house/hotels/areas'),
  getPoiCategories: () => request('/house/hotels/poi-categories'),
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    const qs = q.toString();
    return request(`/house/hotels${qs ? `?${qs}` : ''}`);
  },
  get: (id) => request(`/house/hotels/${id}`),
  sync: () => request('/house/hotels/sync', { method: 'POST' }),
};


const api = { productsAPI, categoriesAPI, blogAPI, aiAPI, scrapingAPI, exportAPI, loiAPI, tenderAPI, analyticsAPI, authAPI, scenarioAPI, contactsAPI, emailCampaignAPI, landingPageAPI, chatbotAPI, funnelAPI, gdriveAPI, systemConfigAPI, googleAdsAPI, facebookAPI, miraAPI, videoAPI, marketingSiteAPI, aeoScannerAPI, citationMonitorAPI, billingAPI, businessModuleAPI, scenarioSiteAdminAPI, scenarioSiteAPI, lineAPI, embotAPI, schedulerAPI, aiSalesAPI, partnerPortalAPI, partnershipAPI, backupAPI, trendsAPI, devRequestsAPI, aeoFixAPI, devSpecAPI, officeAPI, homeAPI, hotelAPI };
export default api;
